import { useState, useEffect, useCallback, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import configData from "../../config/config.json";
import { PublishEventTableData } from '../events/events';
import { SUCCESS_REPLY, ERROR_REPLY, EVENT_ADD, EVENT_TOPIC_FOR_DATAFEED_SERVER, JSON_KEY_BROWSER_SESSION_ID,
    GetFormattedTimestamp,
  } from '../../common/common'
import { HandleExchangeReply, HandleIndexMembersReply, HandleIndicesReply, HandleInstrumentReply, HandleMarketReply, HandleOrderBookReply, HandlePublicTradesReply, HandleTradingEventReply } from './datafeed_messages';
import { useDispatch, useSelector } from 'react-redux';
import { selectDFUserId, selectInstrumentsLoaded, setInstrumentsLoaded } from '../../redux/globalsSlice';
import { MSG_TYPE_EXCHANGE_REPLY, MSG_TYPE_INDEX_MEMBERS_REPLY, MSG_TYPE_INDICES_REPLY, MSG_TYPE_MARKET_REPLY, MSGTYPE_DF_LOGON, MSGTYPE_HEARTBEAT, MSGTYPE_INSTRUMENT_REPLY, MSGTYPE_ORDERBOOK_REPLY, MSGTYPE_PUBLIC_TRADES_REPLY, MSGTYPE_TRADING_EVENT_REPLY } from '../../common/msg_types';

const TEST_REQ_ID               = 'test';

export const DFConnection = () => {
    const [socketUrl] = useState(configData.DATAFEED_URL);
    const [loggedOn, setLoggedOn] = useState(false); 
    const dispatch = useDispatch();

     console.log("Transaction WebSocket URL:", configData.DATAFEED_URL);
    // to always have the latest value of dfUserId
    const dfUserId = useSelector(selectDFUserId);
    const dfUserIdRef = useRef(dfUserId);
    useEffect(() => {
        dfUserIdRef.current = dfUserId;
    }, [dfUserId]);
    
    // redux timing issue, having a delay until redux has sent the instruments data to all componenets
    const isInstrumentsLoaded = useSelector(selectInstrumentsLoaded);
    const pendingMessagesRef = useRef([]);
    
    const { sendMessage, lastMessage, readyState } = useWebSocket(socketUrl, 
        {
            heartbeat: {
                message: JSON.stringify({m_type: MSGTYPE_HEARTBEAT, [TEST_REQ_ID]: "ABCDE"}),
                returnMessage: JSON.stringify({m_type: MSGTYPE_HEARTBEAT, [TEST_REQ_ID]: "ABCDE"}),
                timeout: 60000, // 1 minute, if no response is received, the connection will be closed
                interval: 30000, // every 30 seconds, a ping message will be sent
                shouldReconnect: () => true, // Auto-reconnect on close
                reconnectAttempts: Infinity,
                reconnectInterval: (attempt) => Math.min(attempt * 1000, 5000),
            },
            retryOnError: true,
            reconnectAttempts: 10,
            reconnectInterval: 3000,
            onClose: () => console.log("WebSocket disconnected"),
        }
    ); 

    const connectionStatus = {
        [ReadyState.CONNECTING]: 'Connecting',
        [ReadyState.OPEN]: 'Open',
        [ReadyState.CLOSING]: 'Closing',
        [ReadyState.CLOSED]: 'Closed',
        [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
    }[readyState];

    const sendMessageRef = useRef(sendMessage);

    useEffect(() => {
        sendMessageRef.current = sendMessage;
    }, [sendMessage]);

    const PrepareToSendMessage = useCallback ((message) => {
        message[JSON_KEY_BROWSER_SESSION_ID] = GetFormattedTimestamp(); // always add browser session id
        
        console.log('DF PrepareToSendMessage: ' + JSON.stringify(message));
        sendMessage(JSON.stringify(message));
        PublishEventTableData(EVENT_ADD, JSON.stringify(message));
    }, [sendMessage]);
    
    useEffect(() => {
        // Event Handlers
        const handleEvent = (event) => {
            const { data } = event.detail;
            // console.log('EVENT_TOPIC_FOR_DATAFEED_SERVER, type: ', type, 'sub_type: ', sub_type, ', data: ', data, 'user: ', data.ts_user_id);
            PrepareToSendMessage(data);
        };

        window.addEventListener(EVENT_TOPIC_FOR_DATAFEED_SERVER, handleEvent);

        return () => {
            window.removeEventListener(EVENT_TOPIC_FOR_DATAFEED_SERVER, handleEvent);
        };
    }, [PrepareToSendMessage]);

    const ProcessMessage = useCallback ((event) => {
        // console.log('Datafeed New message: ', event.data);
        const json_message = JSON.parse(event.data);
        const { cmd, m_type } = json_message;

        if ( json_message.m_type) {
            switch(m_type) {
                case MSG_TYPE_EXCHANGE_REPLY:
                    HandleExchangeReply(cmd, json_message);
                    break;
                case MSGTYPE_INSTRUMENT_REPLY:
                    HandleInstrumentReply(cmd, json_message);
                    break;
                case MSG_TYPE_INDICES_REPLY:
                    HandleIndicesReply(cmd, json_message);
                    break;
                case MSG_TYPE_INDEX_MEMBERS_REPLY:
                    HandleIndexMembersReply(cmd, json_message);
                    break;
                case MSG_TYPE_MARKET_REPLY:
                    HandleMarketReply(cmd, json_message);
                    break;
                case MSGTYPE_ORDERBOOK_REPLY:
                    HandleOrderBookReply(cmd, json_message);
                    break;
                case MSGTYPE_TRADING_EVENT_REPLY:
                    HandleTradingEventReply(cmd, json_message);
                    break;
                case MSGTYPE_PUBLIC_TRADES_REPLY:
                    HandlePublicTradesReply(cmd, json_message);
                    break;

                case SUCCESS_REPLY:
                    PublishEventTableData(EVENT_ADD, event.data);
                    switch (json_message.orig_m_type) {
                        case MSGTYPE_DF_LOGON:
                            console.log('Successful DF Logon');
                            setLoggedOn(true);
                            break;
                        
                        default:
                            console.log('Unknown original_m_type or not set: ' + json_message.orig_m_type)
                            break;  
                    }
                    break;

                case ERROR_REPLY:
                    PublishEventTableData(EVENT_ADD, event.data);
                    console.log('Error message: ' + json_message.m_type + ', data: ' + event.data);
                    break;

                default:
                    console.log('DATAFEED: Unknown message type: ' + json_message.m_type + ', data: ' + event.data);
            }
        }
        else {
            console.log('Missing json field m_type')
        }
    }, []);

    // useEffect(() => {
    //     //console.log('lastMessage: ' + lastMessage + 'connectionStatus: ' + connectionStatus);
    //     if (lastMessage !== null) {
    //         //console.log('DF lastMessage: ' + lastMessage.data);
    //         //setMessageHistory((prev) => prev.concat(lastMessage));
    //         ProcessMessage(lastMessage);
    //     }
    //     if (connectionStatus === 'Closed') {
    //         console.log('Datafeed Webscoket Status is closed!');
    //     }
    // }, [lastMessage, connectionStatus, ProcessMessage]);

    useEffect(() => {
        if (lastMessage !== null) {
            const json_message = JSON.parse(lastMessage.data);
            const type = json_message.m_type;

            if ((type === MSGTYPE_INSTRUMENT_REPLY) || (type === SUCCESS_REPLY) || (type === MSGTYPE_HEARTBEAT) || (type === ERROR_REPLY)) {
                ProcessMessage(lastMessage);
            } else if ((type === MSGTYPE_TRADING_EVENT_REPLY) && (!isInstrumentsLoaded)) {
                setTimeout(() => {
                    dispatch(setInstrumentsLoaded(true));
                }, 2000); // 2 seconds delay so redux can sync everywhere
                ProcessMessage(lastMessage);
            } else if (isInstrumentsLoaded) {
                ProcessMessage(lastMessage);
            } else {
                pendingMessagesRef.current.push(lastMessage);
            }
        }

        if (connectionStatus === 'Closed') {
            console.log('Transaction Websocket Status is closed!');
        }

        }, [lastMessage, ProcessMessage, connectionStatus, isInstrumentsLoaded, dispatch]);

        useEffect(() => {
            if (isInstrumentsLoaded && pendingMessagesRef.current.length > 0) {
                pendingMessagesRef.current.forEach(msg => ProcessMessage(msg));
                pendingMessagesRef.current = [];
            }

        }, [isInstrumentsLoaded, ProcessMessage]);
        
    return (
        [connectionStatus, loggedOn]
    );
};